"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, FlaskConical, ScanLine, Warehouse } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const applicationsData = [{
  id: "raw-material-verification",
  title: "Raw Material Verification",
  badge: "Quality Control",
  icon: FlaskConical,
  description: "Rapid non-destructive identification of incoming active substances, excipients, and chemical intermediates directly in quarantine or staging areas.",
  points: ["Immediate pass/fail verification against approved spectral libraries", "Analysis through transparent packaging and containers", "Compliant documentation for regulated GMP workflows"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_verification.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_verification.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_verification.webp-wvc-sizes",
  link: "/RawMaterialIdentificationPage",
  ctaText: "Explore Raw Material ID"
}, {
  id: "field-mobile-identification",
  title: "Field & Mobile Identification",
  badge: "Portable & Handheld",
  icon: ScanLine,
  description: "On-site chemical and material identification for mobile technicians, research personnel, and rapid response safety operations.",
  points: ["Ergonomic handheld operation in non-laboratory settings", "Pre-configured reference databases and library management", "Minimal sample preparation requirements"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/field_mobile_identification.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/field_mobile_identification.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/field_mobile_identification.png-wvc-sizes",
  link: "/RamanHandheldAnalyzersPage",
  ctaText: "View Mobile Analyzers"
}, {
  id: "warehouse-dock-inspection",
  title: "Warehouse & Loading Dock Inspection",
  badge: "Logistics & Receiving",
  icon: Warehouse,
  description: "Point-of-receipt sampling and container verification at loading bays and distribution hubs to streamline receiving workflows before warehouse transfer.",
  points: ["Reduces transfer bottlenecks and sample hold times", "Direct verification at the receiving dock", "Eliminates transit contamination risks"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/warehouse_dock_inspection.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/warehouse_dock_inspection.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/warehouse_dock_inspection.png-wvc-sizes",
  link: "/DiscussYourApplicationPage",
  ctaText: "Discuss Workflow Integration"
}];
export default function Applications() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="applications" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-t border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-4">
            Practical Deployments
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl text-foreground font-default">
            Targeted Applications Across the Lifecycle
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Analytical instrumentation and qualified workflows tailored to routine testing, mobile verification, and receiving inspection across regulated manufacturing environments.
          </p>
        </div>

        {/* Applications Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {applicationsData.map((app, index) => {
          const IconComponent = app.icon;
          return <div key={app.id} data-index={index} className="flex">
                <motion.div className="flex flex-col w-full" initial={shouldReduceMotion ? {
              opacity: 1,
              y: 0
            } : {
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: "-50px"
            }} transition={{
              duration: 0.4,
              delay: index * 0.1
            }}>
                  <Card className="flex flex-col h-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-200">
                    {/* Image Header */}
                    <div className="relative aspect-16/10 w-full overflow-hidden rounded-t-xl bg-muted">
                      <img src={app.image} data-wvc-srcset={app.srcset} data-wvc-sizes={app.sizes} alt={app.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                      <div className="absolute top-3 left-3">
                        <Badge variant="secondary" className="bg-background/90 backdrop-blur-xs text-foreground font-medium">
                          {app.badge}
                        </Badge>
                      </div>
                    </div>

                    <CardHeader className="gap-2">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-foreground">
                          <IconComponent className="h-5 w-5 text-primary" />
                        </div>
                        <CardTitle className="text-xl font-semibold text-foreground font-default">
                          {app.title}
                        </CardTitle>
                      </div>
                      <CardDescription className="text-sm text-muted-foreground leading-normal mt-1 font-default">
                        {app.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="flex-1">
                      <div className="space-y-2.5 pt-2 border-t border-border">
                        {app.points.map((point, pIndex) => <div key={pIndex} data-index={pIndex} className="flex items-start gap-2 text-sm text-muted-foreground font-default">
                            <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                            <span>{point}</span>
                          </div>)}
                      </div>
                    </CardContent>

                    <CardFooter className="pt-2">
                      <Button asChild variant="outline" className="w-full justify-between group">
                        <Link to={app.link}>
                          <span>{app.ctaText}</span>
                          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                </motion.div>
              </div>;
        })}
        </div>

        {/* Bottom Support Banner */}
        <div className="mt-12 sm:mt-16 rounded-xl border border-border bg-secondary/50 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h3 className="text-lg font-semibold text-foreground font-default">
              Need a feasibility study for a custom application?
            </h3>
            <p className="mt-1 text-sm text-muted-foreground font-default">
              Our specialists evaluate samples, verify method suitability, and provide complete documentation support for regulated testing requirements.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button asChild variant="default">
              <Link to="/FeasibilityStudiesPage">
                Request Feasibility Study
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/ContactPage">
                Contact Team
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}